import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { apartmentsApi, ApartmentFeeCategory } from '../../api/apartmentsApi';
import { isMeterBasedFeeCategory } from '../../utils/feeCategoryBillingKind';
import { submitApartmentServiceFeeInvoice } from '../../utils/submitApartmentServiceFeeInvoice';
import AddFixedServiceFeeModal from '../modals/AddFixedServiceFeeModal';
import AddUsageServiceFeeModal from '../modals/AddUsageServiceFeeModal';
import AddOtherServiceFeeModal from '../modals/AddOtherServiceFeeModal';
import { AddCasualParkingFeeModal } from './AddCasualParkingFeeModal';
import { isCasualParkingFeeCode } from '../../utils/feeCategoryCodeGrouping';
import { buildCompositeBillServiceLines } from '../../utils/feeCategoryCompositePricing';
import { feeCategoriesApi } from '../../api/feeCategoriesApi';
import { assertCanAddManualFixedParkingInvoice } from '../../utils/manualParkingInvoiceGuard';

interface AddServiceFeeInvoiceLauncherProps {
  apartmentId: string;
  buildingId?: string;
  onAdded?: () => void;
  className?: string;
}

export const AddServiceFeeInvoiceLauncher: React.FC<AddServiceFeeInvoiceLauncherProps> = ({
  apartmentId,
  buildingId,
  onAdded,
  className = '',
}) => {
  const [feeCategories, setFeeCategories] = useState<ApartmentFeeCategory[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState<ApartmentFeeCategory | null>(null);
  const [showFixedModal, setShowFixedModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [showCasualParkingModal, setShowCasualParkingModal] = useState(false);
  const [hasCasualParkingCategory, setHasCasualParkingCategory] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!apartmentId) return;
    apartmentsApi
      .getAllApartmentFeeCategories(apartmentId)
      .then(setFeeCategories)
      .catch(() => setFeeCategories([]));
  }, [apartmentId]);

  useEffect(() => {
    if (!buildingId) {
      setHasCasualParkingCategory(false);
      return;
    }
    feeCategoriesApi
      .getAll(buildingId)
      .then((list) => setHasCasualParkingCategory(list.some((fc) => fc.feeCode === 'T-VL001')))
      .catch(() => setHasCasualParkingCategory(false));
  }, [buildingId]);

  const categoryOptions = useMemo(
    () =>
      feeCategories.filter(
        (fc) =>
          fc.feeCategory?.isActive &&
          fc.feeCategory.pricingType !== 'QUOTA_BASED' &&
          !isCasualParkingFeeCode(fc.feeCategory.feeCode),
      ),
    [feeCategories],
  );

  useLayoutEffect(() => {
    if (!menuOpen || !buttonRef.current) return;

    const updateMenuPosition = () => {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const menuWidth = 288;
      const maxMenuHeight = 320;
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;
      const availableHeight = openUpward ? spaceAbove : spaceBelow;
      const menuHeight = Math.max(120, Math.min(maxMenuHeight, availableHeight));

      setMenuStyle({
        position: 'fixed',
        top: openUpward ? rect.top - menuHeight - gap : rect.bottom + gap,
        left: Math.max(gap, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - gap)),
        width: menuWidth,
        maxHeight: menuHeight,
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [menuOpen, categoryOptions.length, hasCasualParkingCategory]);

  const getFeeLabel = (fee: ApartmentFeeCategory) => {
    const lines = buildCompositeBillServiceLines(fee.feeCategory.name, fee.feeCategory);
    return lines.length > 0 ? lines.join(' / ') : fee.feeCategory.name;
  };

  const openCategory = async (fee: ApartmentFeeCategory) => {
    setMenuOpen(false);
    if (
      !isMeterBasedFeeCategory(fee.feeCategory) &&
      buildingId
    ) {
      try {
        await assertCanAddManualFixedParkingInvoice({
          apartmentId,
          buildingId,
          feeCategoryId: fee.feeCategoryId,
          feeCode: fee.feeCategory.feeCode,
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Không thể thêm hóa đơn phí gửi xe thủ công';
        toast.error(message);
        return;
      }
    }
    setSelectedFee(fee);
    if (isMeterBasedFeeCategory(fee.feeCategory)) {
      setShowUsageModal(true);
    } else {
      setShowFixedModal(true);
    }
  };

  const handleSave = async (data: Parameters<typeof submitApartmentServiceFeeInvoice>[0]) => {
    try {
      await submitApartmentServiceFeeInvoice(data, feeCategories);
      toast.success('Thêm hóa đơn thành công');
      onAdded?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Có lỗi xảy ra khi thêm hóa đơn');
      throw error;
    }
  };

  const serviceTypeLabel = selectedFee ? getFeeLabel(selectedFee) : '';

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="text-red-600 hover:text-red-700 border border-red-500 hover:border-red-600 px-3 py-2 rounded-lg text-sm font-medium bg-white"
        >
          Thêm hóa đơn
        </button>
        {menuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-[55] cursor-default"
              aria-label="Đóng menu"
              onClick={() => setMenuOpen(false)}
            />
            <div
              style={menuStyle}
              className="z-[56] overflow-y-auto overscroll-y-contain rounded-lg border border-gray-200 bg-white shadow-lg py-1"
            >
              {hasCasualParkingCategory && buildingId && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowCasualParkingModal(true);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-emerald-700 font-medium"
                >
                  Thu tiền xe vãng lai
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setShowOtherModal(true);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-700 font-medium ${
                  hasCasualParkingCategory && buildingId ? 'border-t' : ''
                }`}
              >
                Hóa đơn khác
              </button>
              {categoryOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 border-t">Chưa có loại phí trên căn hộ</div>
              ) : (
                <div className="border-t">
                  {categoryOptions.map((fee) => (
                    <button
                      key={fee.feeCategoryId}
                      type="button"
                      onClick={() => openCategory(fee)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      {getFeeLabel(fee)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <AddFixedServiceFeeModal
        isOpen={showFixedModal}
        onClose={() => {
          setShowFixedModal(false);
          setSelectedFee(null);
        }}
        serviceType={serviceTypeLabel}
        feeCategory={selectedFee}
        apartmentId={apartmentId}
        buildingId={buildingId}
        onSave={handleSave}
      />

      <AddUsageServiceFeeModal
        isOpen={showUsageModal}
        onClose={() => {
          setShowUsageModal(false);
          setSelectedFee(null);
        }}
        serviceType={serviceTypeLabel}
        feeCategory={selectedFee}
        apartmentId={apartmentId}
        buildingId={buildingId}
        onSave={handleSave}
      />

      <AddOtherServiceFeeModal
        isOpen={showOtherModal}
        onClose={() => setShowOtherModal(false)}
        apartmentId={apartmentId}
        onSave={handleSave}
      />

      {buildingId && (
        <AddCasualParkingFeeModal
          isOpen={showCasualParkingModal}
          onClose={() => setShowCasualParkingModal(false)}
          apartmentId={apartmentId}
          buildingId={buildingId}
          onSuccess={() => onAdded?.()}
        />
      )}
    </>
  );
};

export default AddServiceFeeInvoiceLauncher;
